import { useState } from 'react'
import {
  computeEndurance, computeTotalInjuryLevels,
  computeUniversalPenalty,
  computeEffectiveMove, computeEffectiveDodge,
  getArmourTier, calcRawEP, calcCombatEP,
} from '../../lib/computedStats'
import { useActionDispatch } from '../../hooks/useActionDispatch'

// Full location name → abbreviated code for display
const LOCATION_ABBREV: Record<string, string> = {
  'Skull': 'Sk', 'Face': 'Fa', 'Neck': 'Nk',
  'Shoulder-L': 'Sh', 'Shoulder-R': 'Sh',
  'Upper-Arm-L': 'Ua', 'Upper-Arm-R': 'Ua',
  'Elbow-L': 'El', 'Elbow-R': 'El',
  'Forearm-L': 'Fw', 'Forearm-R': 'Fw',
  'Hand-L': 'Ha', 'Hand-R': 'Ha',
  'Thorax': 'Tx', 'Abdomen': 'Ab', 'Groin': 'Gr',
  'Hip-L': 'Hp', 'Hip-R': 'Hp',
  'Thigh-L': 'Th', 'Thigh-R': 'Th',
  'Knee-L': 'Kn', 'Knee-R': 'Kn',
  'Calf-L': 'Cf', 'Calf-R': 'Cf',
  'Foot-L': 'Ft', 'Foot-R': 'Ft',
}

function abbreviateCoverage(coverage: string | string[] | null): string {
  if (!coverage) return '—'
  const arr = Array.isArray(coverage) ? coverage : coverage.split(/\s+/)
  // Deduplicate abbreviated codes (Shoulder-L + Shoulder-R both → Sh)
  const codes = [...new Set(arr.map(loc => LOCATION_ABBREV[loc] ?? loc))]
  return codes.join(' ')
}

interface Props {
  character: {
    attr_str: number; attr_sta: number; attr_dex: number; attr_agl: number
    attr_int: number; attr_aur: number; attr_wil: number
    fatigue_levels: number
    injuries: { injury_levels: number; healed_at: string | null }[]
    weapons: {
      id: string; weapon_name: string; ml: number; attack_class: number
      defence_class: number; impact_b: number | null; impact_e: number | null
      impact_p: number | null; is_equipped: boolean; weight_lbs: number
      hand_mode_penalty: number; weapon_quality: number | string | null
      is_primary: boolean; held_in: 'right' | 'left' | null; two_handed: boolean
    }[]
    armour: {
      id: string; item_name: string; material_id: string | null
      coverage: string | string[] | null; quality_mod: number
      weight_lbs: number; is_worn: boolean
    }[]
    equipment: { weight_lbs: number | null; quantity?: number; is_carried: boolean }[]
  }
  physicalPenalty: number
  armourProtection?: Record<string, { blunt: number; edge: number; point: number; fire: number }>
  characterId?: string
  campaignId?: string
  canEdit?: boolean
}

export function CombatPanel({ character, physicalPenalty, armourProtection, characterId, campaignId, canEdit }: Props) {
  const { dispatch } = useActionDispatch(characterId ?? '', campaignId ?? '')
  const [toggling, setToggling] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)

  const conditions: string[] = (character as any).conditions ?? []
  const isProne = conditions.includes('prone')

  const run = async (key: string, fn: () => Promise<unknown>) => {
    setBusy(key)
    try { await fn() } catch { /* */ }
    setBusy(null)
  }

  const endurance = computeEndurance(character)
  const totalInjury = computeTotalInjuryLevels(character.injuries)
  const up = computeUniversalPenalty(totalInjury, character.fatigue_levels)
  const pp = physicalPenalty
  const effMove = computeEffectiveMove(character.attr_agl, pp)
  // Dodge uses Dodge skill ML, not raw AGL
  const dodgeSkill = (character as any).skills?.find((s: any) => s.skill_name === 'Dodge')
  const dodgeML = dodgeSkill?.ml ?? character.attr_agl * 3  // fallback: SB×3 if no skill row
  const effDodge = computeEffectiveDodge(dodgeML, up)

  // Unarmed combat — punch, kick, head butt (HM3 RAW values)
  const unarmedSkill = (character as any).skills?.find((s: any) => s.skill_name === 'Unarmed')
  const unarmedML = unarmedSkill?.ml ?? Math.floor((character.attr_str + character.attr_dex + character.attr_agl) / 3) * 4
  const unarmedEntries = [
    { id: 'unarmed-punch', weapon_name: 'Punch', ml: unarmedML, attack_class: 0, defence_class: 15, impact_b: 0, impact_e: null, impact_p: null, hand_mode_penalty: 0, is_equipped: true, is_primary: false, weapon_quality: null, held_in: null as ('left'|'right'|null), two_handed: false },
    { id: 'unarmed-kick', weapon_name: 'Kick', ml: unarmedML, attack_class: 5, defence_class: 5, impact_b: 1, impact_e: null, impact_p: null, hand_mode_penalty: 0, is_equipped: true, is_primary: false, weapon_quality: null, held_in: null as ('left'|'right'|null), two_handed: false },
  ]

  // In Hand: currently gripped. At Belt: equipped but not in hand. Stowed: not equipped.
  // Exclude held_in weapons from stowed to avoid double-display when is_equipped gets out of sync.
  const inHand  = character.weapons.filter(w => w.held_in != null)
  const belt    = character.weapons.filter(w => w.held_in == null && w.is_equipped)
  const stowedWeapons = character.weapons.filter(w => w.held_in == null && !w.is_equipped)

  const handLabel = (w: { held_in: 'right' | 'left' | null; two_handed: boolean }) => {
    if (w.two_handed) return ' (2H)'
    if (w.held_in === 'right') return ' (R)'
    if (w.held_in === 'left')  return ' (L)'
    return ''
  }
  const wornArmour = character.armour.filter(a => a.is_worn)
  const stowedArmour = character.armour.filter(a => !a.is_worn)

  const toggleArmour = async (a: { id: string; is_worn: boolean }) => {
    if (!characterId || !campaignId || toggling) return
    setToggling(a.id)
    try {
      if (a.is_worn) await dispatch('UNEQUIP_ARMOUR', { armour_id: a.id })
      else await dispatch('EQUIP_ARMOUR', { armour_id: a.id })
    } catch { /* */ }
    setToggling(null)
  }

  // Get protection values for an armour piece from its material
  const getPieceProt = (a: { material_id: string | null; quality_mod: number }): string => {
    if (!a.material_id || !armourProtection) return ''
    // Look up base material protection from the first matching location
    // (all locations for same material have same base values)
    const MATERIAL_BASE: Record<string, [number, number, number, number]> = {
      'Cloth': [1,1,1,1], 'Quilt': [5,3,2,4], 'Leather': [2,4,3,3],
      'Kurbul': [4,5,4,3], 'Ring': [3,6,4,3], 'Mail': [2,8,5,1],
      'Scale': [5,9,4,5], 'Plate': [6,10,6,2],
    }
    const base = MATERIAL_BASE[a.material_id]
    if (!base) return ''
    const q = a.quality_mod
    return `${base[0]+q}/${base[1]+q}/${base[2]+q}/${base[3]+q}`
  }

  return (
    <div className="border border-parchment-dark rounded p-4 mb-4 bg-white">
      <h3 className="font-cinzel font-semibold text-sm text-accent uppercase tracking-wider mb-3 pb-2 border-b-2 border-accent/20">
        Combat Profile
      </h3>

      {/* Hero stats — large display numbers */}
      <div className="grid grid-cols-3 gap-2 text-center mb-4">
        <div className="bg-parchment rounded py-3">
          <div className="text-[10px] text-ink-light uppercase tracking-wider mb-1">Endurance</div>
          <div className="text-4xl font-bold font-cinzel text-ink leading-none">{endurance}</div>
        </div>
        <div className="bg-parchment rounded py-3">
          <div className="text-[10px] text-ink-light uppercase tracking-wider mb-1">Move</div>
          <div className="text-4xl font-bold font-cinzel text-ink leading-none">{effMove}</div>
        </div>
        <div className="bg-parchment rounded py-3">
          <div className="text-[10px] text-ink-light uppercase tracking-wider mb-1">Dodge</div>
          <div className="text-4xl font-bold font-cinzel text-ink leading-none">{effDodge}</div>
        </div>
      </div>

      <div className="flex gap-4 text-sm mb-3 flex-wrap">
        <div><span className="text-ink-light">Fatigue:</span> <span className="font-semibold">{character.fatigue_levels}</span></div>
        <div><span className="text-ink-light">Injury:</span> <span className={`font-semibold ${totalInjury > 0 ? 'text-danger' : ''}`}>{totalInjury}</span></div>
        <div><span className="text-ink-light">Penalty:</span> {pp > 0 ? <span className="font-bold text-danger">−{pp * 5} EML</span> : <span className="text-success">none</span>}</div>
      </div>

      {/* Combat status — prone toggle */}
      {(isProne || canEdit) && (
        <div className="flex flex-wrap gap-2 mb-3">
          {isProne && (
            <span className="text-xs bg-warning/20 text-warning border border-warning/40 px-2 py-1 rounded font-semibold">
              Prone
            </span>
          )}
          {canEdit && (isProne ? (
            <button
              onClick={() => run('prone', () => dispatch('REMOVE_CONDITION', { condition: 'prone' }))}
              disabled={busy === 'prone'}
              className="px-3 py-1 text-xs bg-warning text-white rounded hover:opacity-90 disabled:opacity-50">
              Rise from Prone
            </button>
          ) : (
            <button
              onClick={() => run('prone', () => dispatch('APPLY_CONDITION', { condition: 'prone' }))}
              disabled={busy === 'prone'}
              className="px-3 py-1 text-xs border border-warning text-warning rounded hover:bg-warning hover:text-white disabled:opacity-50">
              Fall Prone
            </button>
          ))}
        </div>
      )}

      {/* Weapons — read-only display; management is in the Weapons panel */}
      {character.weapons.length > 0 && (
        <>
          <h4 className="font-cinzel text-xs text-ink-light uppercase tracking-wider border-t border-parchment-dark pt-2 mt-2 mb-1">Weapons</h4>
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[400px]">
            <thead>
              <tr className="border-b border-parchment-dark text-left">
                <th className="py-1 text-ink-light font-normal">Weapon</th>
                <th className="text-right text-ink-light font-normal hidden sm:table-cell">WQ</th>
                <th className="text-right text-ink-light font-normal">ML</th>
                <th className="text-right font-semibold text-ink">AML</th>
                <th className="text-right font-semibold text-ink">DML</th>
                <th className="text-right text-ink-light font-normal">B</th>
                <th className="text-right text-ink-light font-normal">E</th>
                <th className="text-right text-ink-light font-normal">P</th>
                {canEdit && <th className="w-12"></th>}
              </tr>
            </thead>
            <tbody>
              <tr><td colSpan={canEdit ? 9 : 8} className="pt-1 text-ink-light text-[10px] uppercase">In Hand</td></tr>
              {[...inHand, ...unarmedEntries].map(w => (
                <tr key={w.id} className="border-b border-parchment/50">
                  <td className="py-1 font-medium">
                    {w.weapon_name}
                    {w.is_primary && <span className="text-accent ml-1">*</span>}
                    <span className="text-ink-light">{handLabel(w)}</span>
                    {!w.two_handed && w.hand_mode_penalty < 0 && <span className="text-danger ml-1">−{Math.abs(w.hand_mode_penalty)}</span>}
                  </td>
                  <td className="text-right text-ink-light hidden sm:table-cell">{w.weapon_quality ?? ''}</td>
                  <td className="text-right text-ink-light">{w.ml}</td>
                  <td className="text-right font-bold text-base text-ink">{Math.max(5, w.ml + w.attack_class + (w.two_handed ? 0 : (w.hand_mode_penalty ?? 0)) - pp * 5)}</td>
                  <td className="text-right font-bold text-base text-ink">{Math.max(5, w.ml + w.defence_class + (w.two_handed ? 0 : (w.hand_mode_penalty ?? 0)) - pp * 5)}</td>
                  <td className="text-right">{w.impact_b ?? '—'}</td>
                  <td className="text-right">{w.impact_e ?? '—'}</td>
                  <td className="text-right">{w.impact_p ?? '—'}</td>
                  {canEdit && (
                    <td className="text-right">
                      {w.held_in !== null && (
                        <button
                          onClick={() => run(`drop-${w.id}`, () => dispatch('DROP_WEAPON_FROM_HAND', { weapon_id: w.id }))}
                          disabled={busy === `drop-${w.id}`}
                          className="text-[10px] px-1.5 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50"
                          title="Drop from hand">
                          Drop
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
              {belt.length > 0 && (
                <tr><td colSpan={canEdit ? 9 : 8} className="pt-1 text-ink-light text-[10px] uppercase">At Belt</td></tr>
              )}
              {belt.map(w => (
                <tr key={w.id} className="border-b border-parchment/50">
                  <td className="py-1">
                    {w.weapon_name}
                    {w.is_primary && <span className="text-accent ml-1">*</span>}
                  </td>
                  <td className="text-right hidden sm:table-cell">{w.weapon_quality ?? ''}</td>
                  <td className="text-right">{w.ml}</td>
                  <td className="text-right">{Math.max(5, w.ml + w.attack_class + (w.hand_mode_penalty ?? 0) - pp * 5)}</td>
                  <td className="text-right">{Math.max(5, w.ml + w.defence_class + (w.hand_mode_penalty ?? 0) - pp * 5)}</td>
                  <td className="text-right">{w.impact_b ?? '—'}</td>
                  <td className="text-right">{w.impact_e ?? '—'}</td>
                  <td className="text-right">{w.impact_p ?? '—'}</td>
                  {canEdit && <td></td>}
                </tr>
              ))}
              {stowedWeapons.length > 0 && (
                <tr><td colSpan={canEdit ? 9 : 8} className="pt-1 text-ink-light text-[10px] uppercase">Stowed</td></tr>
              )}
              {stowedWeapons.map(w => (
                <tr key={w.id} className="border-b border-parchment/30 text-ink-light">
                  <td className="py-0.5">{w.weapon_name}</td>
                  <td className="text-right hidden sm:table-cell">{w.weapon_quality ?? ''}</td>
                  <td className="text-right">{w.ml}</td>
                  <td colSpan={canEdit ? 6 : 5}></td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </>
      )}

      {/* Armour */}
      {character.armour.length > 0 && (
        <>
          <h4 className="font-cinzel text-xs text-ink-light uppercase tracking-wider border-t border-parchment-dark pt-2 mt-2 mb-1">Armour</h4>
          <div className="overflow-x-auto">
          <table className="w-full text-xs min-w-[320px]">
            <thead>
              <tr className="border-b border-parchment-dark text-left">
                <th className="py-1">Piece</th>
                <th>Material</th>
                <th>Coverage</th>
                <th className="text-right">B/E/P/F</th>
                {canEdit && <th className="w-6"></th>}
              </tr>
            </thead>
            <tbody>
              {wornArmour.map(a => (
                <tr key={a.id} className="border-b border-parchment/50">
                  <td className="py-1">
                    {a.item_name}
                    {a.quality_mod !== 0 && <span className="text-ink-light ml-1">({a.quality_mod > 0 ? '+' : ''}{a.quality_mod})</span>}
                  </td>
                  <td>{a.material_id ?? '—'}</td>
                  <td className="text-ink-light">{abbreviateCoverage(a.coverage)}</td>
                  <td className="text-right font-mono">{getPieceProt(a)}</td>
                  {canEdit && (
                    <td className="text-right">
                      <button onClick={() => toggleArmour(a)} disabled={toggling === a.id}
                        className="text-[10px] px-1 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50">-</button>
                    </td>
                  )}
                </tr>
              ))}
              {stowedArmour.length > 0 && (
                <tr><td colSpan={canEdit ? 5 : 4} className="pt-1 text-ink-light text-[10px] uppercase">Stowed</td></tr>
              )}
              {stowedArmour.map(a => (
                <tr key={a.id} className="border-b border-parchment/30 text-ink-light">
                  <td className="py-0.5">{a.item_name}</td>
                  <td>{a.material_id ?? '—'}</td>
                  <td colSpan={2}></td>
                  {canEdit && (
                    <td className="text-right">
                      <button onClick={() => toggleArmour(a)} disabled={toggling === a.id}
                        className="text-[10px] px-1 py-0.5 border border-parchment-dark rounded hover:bg-parchment-dark disabled:opacity-50">+</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
          </div>

          {/* Armour skill in use + EP reduction */}
          {(() => {
            if (wornArmour.length === 0) return null
            const tier = getArmourTier(wornArmour as any)
            if (tier === 'none') return null
            const skillName = tier === 'harness' ? 'Armour Harness'
                            : tier === 'mail'    ? 'Armour Mail'
                            : 'Armour Light'
            const armourML = (character as any).skills?.find((s: any) => s.skill_name === skillName)?.ml ?? 0
            const armourWeightLbs = wornArmour.reduce((sum: number, a: any) => sum + (a.weight_lbs ?? 0), 0)
            const fl = character.fatigue_levels ?? 0
            const rawEP    = calcRawEP(armourWeightLbs, endurance, fl)
            const rawEPnoFL = calcRawEP(armourWeightLbs, endurance, 0)
            const combatEP = calcCombatEP(rawEP, armourML)
            const fatigueDelta = rawEP - rawEPnoFL
            return (
              <div className="mt-2 pt-2 border-t border-parchment/50 text-xs">
                <span className="font-semibold">{skillName}</span>
                {armourML > 0
                  ? <span className="text-ink-light ml-1">(ML {armourML}) — EP {rawEP} → <span className="font-semibold">{combatEP}</span></span>
                  : <span className="text-ink-light ml-1">— no skill (EP {rawEP})</span>
                }
                {fatigueDelta > 0 && (
                  <span className="text-warning ml-2">(fatigue +{fatigueDelta} raw)</span>
                )}
              </div>
            )
          })()}

          {/* Protection by Location (Option D) */}
          {armourProtection && Object.keys(armourProtection).length > 0 && (
            <div className="mt-2 pt-2 border-t border-parchment/50">
              <div className="text-[10px] font-semibold text-ink-light uppercase mb-1">Protection by Location</div>
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs font-mono">
                {(() => {
                  // Group paired locations (L/R have identical protection)
                  const seen = new Set<string>()
                  const entries: Array<{ label: string; prot: { blunt: number; edge: number; point: number; fire: number } }> = []
                  const PAIR_LABEL: Record<string, string> = {
                    'Shoulder-L': 'Shoulder', 'Shoulder-R': 'Shoulder',
                    'Upper-Arm-L': 'Upper Arm', 'Upper-Arm-R': 'Upper Arm',
                    'Elbow-L': 'Elbow', 'Elbow-R': 'Elbow',
                    'Forearm-L': 'Forearm', 'Forearm-R': 'Forearm',
                    'Hand-L': 'Hand', 'Hand-R': 'Hand',
                    'Hip-L': 'Hip', 'Hip-R': 'Hip',
                    'Thigh-L': 'Thigh', 'Thigh-R': 'Thigh',
                    'Knee-L': 'Knee', 'Knee-R': 'Knee',
                    'Calf-L': 'Calf', 'Calf-R': 'Calf',
                    'Foot-L': 'Foot', 'Foot-R': 'Foot',
                  }
                  for (const [loc, prot] of Object.entries(armourProtection)) {
                    const label = PAIR_LABEL[loc] ?? loc
                    if (seen.has(label)) continue
                    seen.add(label)
                    entries.push({ label, prot })
                  }
                  return entries.map(({ label, prot }) => (
                    <div key={label} className="flex justify-between">
                      <span className="text-ink-light">{label}</span>
                      <span>B{prot.blunt} E{prot.edge} P{prot.point} F{prot.fire}</span>
                    </div>
                  ))
                })()}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}
